import { Col, Form, Row } from "react-bootstrap";
import { FormEvent, useEffect, useState } from 'react'
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from 'react-router-dom';
import { ApiErrorResponse } from "../../../../types/ApiErrorResponse";
import AlertMessage from "../../../../components/AlertMessage";
import { useAlert } from "../../../../hooks/useAlert";
import { IPsListNewItemDto } from "../types";
import { editSecurityIPS, getSecurityIPById, saveSecurityIP } from "../api/securityIPSAPI";
import { getSecurityIPFormSchema } from "../schema/securityIPSchema";
import { GenericSaveButton } from "../../../../components/common/GenericSaveButton";

export default function SecurityIPsForm() {
    const { t } = useTranslation(['common','event']);
    const navigate = useNavigate();

    const { ipId } = useParams<{ ipId: string }>();
    const isEditMode = Boolean(ipId);

    const { alert, hideAlert, showAlert, isVisible } = useAlert({ fromLocation: false });

    const [errors, setErrors] = useState<{ [key: string]: string | undefined }>({});
    const [securityIPForm, setSecurityIPForm] = useState<IPsListNewItemDto>({ipOrigin : "", isActive: false })

    const changeInputField = (field: keyof IPsListNewItemDto) => (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>
    ) => {
        setSecurityIPForm({
            ...securityIPForm,
            [field]: e.target.value
        });
    };

    useEffect(() => {
        if (ipId) {
            getSecurityIPById(ipId)
                .then(data => setSecurityIPForm(data))
                .catch((err: ApiErrorResponse) => {
                    console.error(err);
                });
        }
    }, [ipId]);

    const handleSubmit = async (e?: FormEvent) => {
        e?.preventDefault();
        const schema = getSecurityIPFormSchema(t);

        try {
            await schema.validate(securityIPForm, { abortEarly: false });
        } catch (err: any) {
            const validationErrors: { [key: string]: string | undefined } = {};
            err.inner?.forEach((error: any) => {
                if (error.path) {
                    validationErrors[error.path] = error.message;
                }
            });
            setErrors(validationErrors);
            return;
        }

        try {
            if (isEditMode && ipId) {

                await editSecurityIPS(parseInt(ipId), securityIPForm);

                navigate('/admin/ips', {
                    state: { message: t("securityIPs:modal.securityIPsEditSuccessMissage"), shouldRefresh: true }
                });
            } else {

                await saveSecurityIP(securityIPForm);
                navigate('/admin/ips', {
                    state: { message: t("securityIPs:modal.newSecurityIPsSaved"), shouldRefresh: true }
                });
            }

        } catch (error) {
            const fetchError = error as ApiErrorResponse;
            const errorMessage = fetchError?.message
            showAlert({
                message: errorMessage,
                type: "danger",
                duration: 5000,
            });

        }
    }

    const handleComponentToggle = (field: keyof IPsListNewItemDto) => (
        e: React.ChangeEvent<HTMLInputElement>
    ) => {
        setSecurityIPForm({
            ...securityIPForm,
            [field]: e.target.checked 
        });
    };

    return (
        <>
            <Row className="mb-2">
                <Col xs={12} md={8}>
                    <h2>{ipId ? t("securityIPs:newForm.buttonEditSecurityIP") : t("securityIPs:newForm.buttonNewSecurityIP")}</h2>
                </Col>
                <Col xs={12} md={4} className="text-end mt-2 mt-md-0">
                    <GenericSaveButton
                        onClick={() => handleSubmit()}
                        onCancelClick={() => navigate('/admin/ips')}
                        cancelLabel={isEditMode ? t("common:buttons.buttonCancel") : t("common:buttons.buttonBack")}
                    />
                </Col>
                {isVisible && (
                    <AlertMessage
                        message={alert!.message}
                        type={alert!.type}
                        onClose={hideAlert}
                    />
                )}
            </Row>
            <Row>
                <Col>
                    <Form>
                        <Row>
                            <Col xs={3} md={3}>
                                <Form.Group>
                                    <Form.Label>{t("securityIPs:newForm.headerTableSecurityIPId")} *</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={securityIPForm.id}
                                        onChange={changeInputField('id')}
                                        disabled
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col xs={3} md={3}>
                                <Form.Group>
                                    <Form.Label>{t("securityIPs:newForm.headerTableSecurityIPOrigin")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={securityIPForm.ipOrigin}
                                        onChange={changeInputField('ipOrigin')}
                                        isInvalid={!!errors["ipOrigin"]}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.ipOrigin || ""}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col xs={3} md={3}>
                                <Form.Group>
                                    <Form.Label>{t("securityIPs:newForm.headerTableSecurityIPDestiny")}</Form.Label>
                                    <Form.Control
                                        type="text"
                                        value={securityIPForm.ipDestiny}
                                        onChange={changeInputField('ipDestiny')}
                                        isInvalid={!!errors["ipDestiny"]}
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        {errors.ipDestiny || ""}
                                    </Form.Control.Feedback>
                                </Form.Group>
                            </Col>
                            <Col xs={12} md={12}>
                            <Form.Check
                                    type="checkbox"
                                    id="isActive"
                                    label={t("securityIPs:newForm.headerTableSecurityIPIsActive")}
                                    checked={securityIPForm.isActive}
                                    onChange={(e) => handleComponentToggle('isActive')(e)}
                                />
                            </Col>
                        </Row>
                    </Form>
                </Col>
            </Row>
        </>
    );
}



